import { FluxAccountHolder, FluxPartyType } from '@energy-retailer/constants';
import { z } from 'zod';

import {
  isValidateAbn,
  validateDateOfBirth,
  validatePhoneNumber,
} from '../utils';

export const givenNameSchema = z
  .string()
  .min(1, 'Please enter your first name')
  .max(126)
  .regex(
    /^[a-z\-\s]+$/i,
    'First name should only contain letters, spaces and -'
  );

export const personalInformationSchema = z
  .object({
    title: z.nativeEnum(FluxAccountHolder),
    givenName: givenNameSchema,
    familyName: z
      .string()
      .min(1, 'Please enter your last name')
      .max(126)
      .regex(
        /^[a-z\-\s]+$/i,
        'Last name should only contain letters, spaces and -'
      ),
    birthdate: z
      .string()
      .min(1, 'Date is required')
      .superRefine((value, ctx) => {
        const message = validateDateOfBirth(value);
        if (message) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: message,
          });
          return false;
        }
        return true;
      }),
    telephone: z
      .string()
      .min(8, 'Phone number should be at least 8 characters')
      .max(19, 'Phone number should be no more then 19 characters')
      .refine((value) => validatePhoneNumber(value), 'Incorrect area code'),
    email: z
      .string()
      .min(1, 'Please enter your email')
      .email('Email is not valid'),
    reTypyEmail: z
      .string()
      .min(1, 'Please enter your email')
      .email('Email is not valid'),
  })
  .superRefine((value, ctx) => {
    if (value.email !== value.reTypyEmail) {
      ctx.addIssue({
        code: z.ZodIssueCode.invalid_date,
        message:
          'The emails entered do not match. Please ensure that both email entries are identical and try again.',
        path: ['reTypyEmail'],
      });
      return false;
    }
    return true;
  });

export const businessDetailsSchema = z.object({
  organisationName: z.string().min(1, 'Please enter a company name'),
  businessNumber: z
    .string()
    .min(1, 'Please enter an ABN number')
    .refine((value) => isValidateAbn(value), 'Incorrect ABN Number'),
  telephone: z
    .union([
      z
        .string()
        .min(8, 'Phone number should be at least 8 characters')
        .max(19, 'Phone number should be no more then 19 characters')
        .refine((value) => validatePhoneNumber(value), 'Incorrect area code'),
      z.string().length(0),
    ])
    .optional(),
  isTaxRegistered: z
    .boolean()
    .optional()
    .superRefine((value, ctx) => {
      if (value === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Please select an option',
        });
        return false;
      }
      return true;
    }),
});

export const partySchema = z
  .object({
    partyType: z.nativeEnum(FluxPartyType).default(FluxPartyType.Person),
    partyPerson: personalInformationSchema,
    partyOrganisation: businessDetailsSchema.optional(),
  })
  .superRefine((value, ctx) => {
    if (
      value.partyType === FluxPartyType.Organization &&
      !value.partyOrganisation
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Please provide business details',
        path: ['partyOrganisation'],
      });
      return false;
    }
    return true;
  });

export type PartySchema = z.infer<typeof partySchema>;
export type BusinessDetailsSchema = z.infer<typeof businessDetailsSchema>;
export type PersonalInformationSchema = z.infer<
  typeof personalInformationSchema
>;
